/**
 * TypeScript types for Invoice Import feature
 * Based on backend DTOs from TravelCRM.Application.InvoiceImport
 */

import { UserInvoiceCorrections } from './userInvoiceCorrections';

/**
 * Invoice import job status enum
 * Lifecycle: Pending → Processing → [Completed | Failed | Duplicate | RequiresReview | Rejected | Abandoned]
 * Phase 4: Added Rejected and Abandoned statuses
 */
export enum InvoiceImportStatus {
  Pending = 'pending',
  Processing = 'processing',
  Completed = 'completed',
  Failed = 'failed',
  Duplicate = 'duplicate',
  RequiresReview = 'requiresReview',
  Rejected = 'rejected',
  Abandoned = 'abandoned',
}

/**
 * Standardized error codes for invoice import operations
 */
export enum InvoiceImportErrorCode {
  Unknown = 'unknown',
  UnsupportedFormat = 'unsupportedFormat',
  InvalidFileType = 'invalidFileType',
  FileTooLarge = 'fileTooLarge',
  InvalidImageResolution = 'invalidImageResolution',
  ParsingError = 'parsingError',
  MissingMandatoryField = 'missingMandatoryField',
  LowOcrConfidence = 'lowOcrConfidence',
  DuplicateInvoiceNumber = 'duplicateInvoiceNumber',
  DuplicateFileHash = 'duplicateFileHash',
  ExternalServiceError = 'externalServiceError',
  Timeout = 'timeout',
  NetworkError = 'networkError',
  ImporterNotFound = 'importerNotFound',
  ValidationError = 'validationError',
}

/**
 * Severity of a structured InvoiceImportIssueDto (backend: InvoiceImportIssueSeverity, D7/D8),
 * serialized as a camelCase string.
 */
export enum InvoiceImportIssueSeverity {
  Warning = 'warning',
  Error = 'error',
}

export const invoiceImportActionNames = ['approve', 'reject', 'retry', 'abandon'] as const;

export type InvoiceImportActionName = (typeof invoiceImportActionNames)[number];

/**
 * Structured issue raised against a job's parsed invoice data or terminal failure (backend:
 * InvoiceImportIssueDto, R6). `message` is contractually a resolved, stable, code-derived string —
 * never raw parser/provider/exception text (I12).
 */
export interface InvoiceImportIssueDto {
  code: string;
  message: string;
  field: string | null;
  blocksAction: boolean;
  severity: InvoiceImportIssueSeverity;
  parameters: Record<string, string> | null;
  retryable: boolean;
}

export const invoiceImportDirections = ['purchase', 'sales', 'unknown'] as const;

export type InvoiceImportDirection = (typeof invoiceImportDirections)[number];

export const invoiceImportDirectionSources = ['persisted', 'derivedRuntime'] as const;

export type InvoiceImportDirectionSource = (typeof invoiceImportDirectionSources)[number];

export const invoiceImportMatchedSubjectRoles = ['subject1', 'subject2', 'none', 'both'] as const;

export type InvoiceImportMatchedSubjectRole = (typeof invoiceImportMatchedSubjectRoles)[number];

export const invoiceImportFa3Families = [
  'prepayment',
  'finalSettlement',
  'standardCorrection',
  'prepaymentCorrection',
  'settlementCorrection',
  'formalCorrection',
  'collectiveCorrection',
  'mixedTaxation',
  'margin',
  'simplified',
  'intraCommunitySupply',
  'export',
  'standardVat',
] as const;

export type InvoiceImportFa3Family = (typeof invoiceImportFa3Families)[number];

export const invoiceImportFa3Dispositions = ['autoImport', 'reviewRequired'] as const;

export type InvoiceImportFa3Disposition = (typeof invoiceImportFa3Dispositions)[number];

export const invoiceImportFa3SupportStatuses = ['supported', 'unsupported'] as const;

export type InvoiceImportFa3SupportStatus = (typeof invoiceImportFa3SupportStatuses)[number];

export const invoiceImportFa3LineSources = ['faWiersz', 'zamowienieWiersz', 'none'] as const;

export type InvoiceImportFa3LineSource = (typeof invoiceImportFa3LineSources)[number];

export const invoiceImportFa3CorrectionVariants = [
  'beforeAfter',
  'difference',
  'formalOnly',
  'collectivePeriod',
] as const;

export type InvoiceImportFa3CorrectionVariant =
  (typeof invoiceImportFa3CorrectionVariants)[number];

export const invoiceImportFa3LineRoles = [
  'before',
  'after',
  'order',
  'current',
  'delta',
  'referenceOnly',
] as const;

export type InvoiceImportFa3LineRole = (typeof invoiceImportFa3LineRoles)[number];

export const invoiceImportFa3RelationTypes = [
  'correctedDocument',
  'prepaymentDocument',
  'settlementDocument',
] as const;

export type InvoiceImportFa3RelationType = (typeof invoiceImportFa3RelationTypes)[number];

export interface InvoiceImportFa3DocumentLinkDto {
  relationType: InvoiceImportFa3RelationType;
  relatedExpenseInvoiceId: number | null;
  relatedDocumentReference: string | null;
  externalInvoiceNumber: string | null;
  externalKsefNumber: string | null;
  externalIssueDate: string | null;
  isResolved: boolean;
}

export interface InvoiceImportFa3SemanticPreviewLineDto {
  lineRole: InvoiceImportFa3LineRole;
  sourceSection: string;
  sourceLineNumber: number | null;
}

export interface InvoiceImportFa3SemanticPreviewDto {
  family: InvoiceImportFa3Family;
  disposition: InvoiceImportFa3Disposition;
  supportStatus: InvoiceImportFa3SupportStatus;
  lineSource: InvoiceImportFa3LineSource;
  correctionVariant: InvoiceImportFa3CorrectionVariant | null;
  correctionReason: string | null;
  unsupportedReason: string | null;
  links: InvoiceImportFa3DocumentLinkDto[];
  lines: InvoiceImportFa3SemanticPreviewLineDto[];
}

export interface ImportedInvoiceParserMetadataDto {
  sourceDocumentKind?: string | null;
  documentFamily?: string | null;
  lineSource?: string | null;
  correctionVariant?: string | null;
  correctionReason?: string | null;
  sourceKsefNumber?: string | null;
  overlayFlags?: string[];
  provenance?: Record<string, string>;
}

/**
 * Result DTO returned by ImportInvoiceCommand
 */
export interface InvoiceImportResultDto {
  importJobId: number;
  invoiceId: number | null;
  invoiceNumber: string | null;
  status: InvoiceImportStatus;
  statusLabel: string;
  errorMessage: string | null;
  errorCode: InvoiceImportErrorCode | null;
  existingInvoiceId: number | null;
  existingExpenseInvoiceId?: number | null;
  expenseInvoiceId?: number | null;
  createdAt: string;
  /** Operator who created this specific attempt (S4.3b/D19). */
  userId?: number | null;
  /** Position of this job within its retry chain. `0` for the original upload. */
  attemptNumber?: number;
  /** Root job of the entire retry chain (falls back to this job's own id). */
  rootImportJobId?: number;
  previousAttemptImportJobId?: number | null;
  duplicateOfImportJobId?: number | null;
  isRetryable?: boolean;
  /** Structured issues currently raised against the job's parsed invoice data (R6). */
  issues?: InvoiceImportIssueDto[];
  /** Actions the current operator may take right now; renders only from this contract (RM-09). */
  allowedActions?: string[];
}

/**
 * Detailed status DTO returned by GetImportStatusQuery
 * Extends InvoiceImportResultDto with additional metadata
 */
export interface InvoiceImportStatusDto extends InvoiceImportResultDto {
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
  blobStoragePath: string;
  importerType: string | null;
  ocrConfidence: number | null;
  requiresReview: boolean;
  startedAt: string | null;
  completedAt: string | null;
  processingDurationMs: number | null;
  warnings: string[];
  parsedInvoiceData?: ImportedInvoiceDataDto | null;
  fa3SemanticPreview?: InvoiceImportFa3SemanticPreviewDto | null;
  rejectionReason?: string | null;
  rejectedAt?: string | null;
  originalImportJobId?: number | null;
  approvedAt?: string | null;
  approvedByUserId?: number | null;
  rejectedByUserId?: number | null;
  invoiceDirection: InvoiceImportDirection;
  invoiceDirectionReason: string | null;
  matchedSubjectRole: InvoiceImportMatchedSubjectRole | null;
  invoiceDirectionSource: InvoiceImportDirectionSource;
}

/**
 * Parsed invoice data DTO (legacy status payload with frontend-safe extensions)
 */
export interface ImportedInvoiceDataDto {
  invoiceNumber?: string | null;
  issueDate?: string | null;
  saleDate?: string | null;
  placeOfIssue?: string | null;
  type?: string | null;
  scheme?: string | null;
  importerType?: string | null;
  sellerName?: string | null;
  sellerAddress?: string | null;
  sellerNip?: string | null;
  sellerBankAccount?: string | null;
  buyerName?: string | null;
  buyerAddress?: string | null;
  buyerNip?: string | null;
  recipientName?: string | null;
  recipientAddress?: string | null;
  recipientNip?: string | null;
  currency?: string | null;
  currencyRate?: number | null;
  paymentMethod?: string | null;
  isPaid?: boolean | null;
  paymentDueDate?: string | null;
  paidAmount?: number | null;
  paidAt?: string | null;
  paymentComment?: string | null;
  comments?: string | null;
  legalNote?: string | null;
  supplierName?: string | null;
  supplierTaxId?: string | null;
  supplierAddress?: string | null;
  supplierBankAccount?: string | null;
  totalNet?: number | null;
  totalVat?: number | null;
  totalGross?: number | null;
  items?: ImportedInvoiceItemDto[];
  parserMetadata?: ImportedInvoiceParserMetadataDto | null;
}

/**
 * Parsed invoice item DTO
 */
export interface ImportedInvoiceItemDto {
  name?: string | null;
  description?: string | null;
  quantity?: number | null;
  unitNet?: number | null;
  unitGross?: number | null;
  vatRate?: number | null;
  vatType?: string | null;
  isVatExempt?: boolean | null;
  vatExemptNote?: string | null;
  sourceSection?: string | null;
  sourceLineNumber?: number | null;
  isBeforeCorrectionState?: boolean | null;
}

/**
 * History item DTO returned by GetImportHistoryQuery
 * Simplified version for list display
 */
export interface InvoiceImportHistoryDto {
  importJobId: number;
  fileName: string;
  status: InvoiceImportStatus;
  statusLabel: string;
  invoiceNumber: string | null;
  createdAt: string;
  completedAt: string | null;
  importerType?: string | null;
  invoiceDirection: InvoiceImportDirection;
  invoiceDirectionReason: string | null;
  matchedSubjectRole: InvoiceImportMatchedSubjectRole | null;
  invoiceDirectionSource: InvoiceImportDirectionSource;
  userId?: number | null;
  attemptNumber?: number;
  rootImportJobId?: number;
  previousAttemptImportJobId?: number | null;
  duplicateOfImportJobId?: number | null;
  isRetryable?: boolean;
  issues?: InvoiceImportIssueDto[];
  allowedActions?: string[];
}

/**
 * Generic paged list wrapper
 */
export interface PagedListDto<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Request payload for invoice import upload.
 * Note: sourceCountryIso is sent as a query string parameter.
 */
export interface ImportInvoiceRequest {
  formData: FormData;
  sourceCountryIso?: string | null;
}

/**
 * Query parameters for GetImportHistoryQuery
 */
export interface InvoiceImportHistoryParams {
  pageNumber?: number;
  pageSize?: number;
  status?: InvoiceImportStatus | null;
  searchTerm?: string | null;
  importerType?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
}

/**
 * Phase 4: Result DTO for approve import operation
 */
export interface ApproveImportResultDto {
  importJobId: number;
  invoiceId: number | null;
  expenseInvoiceId?: number | null;
  invoiceNumber: string;
  message: string;
}

export interface ApproveImportSubmission {
  userCorrections?: UserInvoiceCorrections | null;
  approveOverride?: boolean;
  approveOverrideReason?: string | null;
}

/**
 * Phase 4: Request payload for approve import
 */
export interface ApproveImportRequest extends ApproveImportSubmission {
  importJobId: number;
}

/**
 * Phase 4: Request payload for reject import
 */
export interface RejectImportRequest {
  importJobId: number;
  reason: string;
}

/**
 * File validation result
 */
export interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

export enum Fa3BackfillMode {
  DryRun = 0,
  Execute = 1,
}

export const fa3BackfillReasonCodes = [
  'MatchedByKsef',
  'MatchedByComposite',
  'MissingReferenceData',
  'NoLocalMatch',
  'SkippedManualFixed',
  'OverrideApplied',
  'RetryEligibleFailed',
  'ApproveEligibleRequiresReview',
  'StatusNotEligible',
  'MissingImportJob',
  'OperationFailed',
  'Unknown',
] as const;

export type Fa3BackfillReasonCode = (typeof fa3BackfillReasonCodes)[number] | string;

export interface RecomputeFa3ForExistingExpenseInvoicesRequest {
  mode: Fa3BackfillMode;
  invoiceIds?: number[];
  dateFrom?: string;
  dateTo?: string;
  includeManualFixed: boolean;
  correlationId?: string;
}

export interface RetryApproveImportJobsRequest {
  mode: Fa3BackfillMode;
  importJobIds: number[];
  correlationId?: string;
}

export interface Fa3SelectiveRecomputeItemDto {
  invoiceId: number;
  sourceKsefNumber: string | null;
  referenceInvoiceNumber: string | null;
  referenceKsefNumber: string | null;
  relationStatus: string;
  reasonCode: Fa3BackfillReasonCode;
}

export interface Fa3SelectiveRecomputeResultDto {
  processedCount: number;
  skippedCount: number;
  failedCount: number;
  warnings: string[];
  items: Fa3SelectiveRecomputeItemDto[];
}

export interface Fa3RetryApproveItemDto {
  importJobId: number;
  previousStatus: string;
  action: string;
  reasonCode: Fa3BackfillReasonCode;
  message: string | null;
}

export interface Fa3RetryApproveResultDto {
  processedCount: number;
  skippedCount: number;
  failedCount: number;
  warnings: string[];
  items: Fa3RetryApproveItemDto[];
}
